import type { PostInputMetaData, PostType } from '../../graphqlTypes'
import type { Draft } from '~publish/legacy/composer/composer/entities/Draft'
import {
  PostTypeGhostPost,
  PostTypePost,
  PostTypeThread,
} from '~publish/legacy/post/constants'

import {
  getChannelVariantLinkAttachment,
  mapChannelVariantThread,
} from '../../channelVariantToCreatePostInput'
import { mapLinkAttachment } from '../../mapAssets'
import { mapThread } from '../../mapThread'
import type { MapperContext } from '../../types'
import { clearable } from '../shared/clearable'
import type { ServiceMapperEntry } from '../types'

// The backend only accepts `post` or `ghost_post` as the Threads post type, the
// multi-post thread is carried by the `thread` array, so the composer's `thread`
// updateType (used purely for editing) maps back to `post`.
const mapThreadsPostType = (value: string | null): PostType | undefined => {
  if (value === PostTypeGhostPost) return PostTypeGhostPost
  if (value === PostTypeThread) return PostTypePost
  return undefined
}

// Threads: location + topic (`channelData.threads`), the thread (via the
// shared `mapThread` helper, omitted for ghost posts since `supportsThread` is
// false for them), and the link attachment are tri-stated via `clearable` —
// omitted on create, `null` on edit to clear them. The post `type` is always
// a concrete value (post / ghost_post), never cleared, so it stays
// omit-when-absent.
//
// `ctx.channelVariant` (when present) supplies `channelData`, `updateType`,
// the thread, and the link attachment. `supportsThread` is a pure function of
// `updateType` for this service, so it's called as-is regardless of source.
export function mapThreads(
  draft: Draft,
  ctx: MapperContext,
): PostInputMetaData {
  const variant = ctx.channelVariant
  const data = variant
    ? variant.channelData?.threads
    : draft.channelData?.threads
  const updateType = variant ? variant.updateType : draft.updateType
  const thread = draft.service.supportsThread(updateType)
    ? variant
      ? mapChannelVariantThread(variant.threadItems, 'threads')
      : mapThread(draft)
    : undefined
  const linkAttachment = variant
    ? getChannelVariantLinkAttachment(variant)
    : mapLinkAttachment(draft)
  const type = mapThreadsPostType(updateType)

  return {
    threads: {
      locationId: clearable(data?.location_id, ctx),
      locationName: clearable(data?.location_name, ctx),
      topic: clearable(data?.topic, ctx),
      thread: clearable(thread, ctx),
      linkAttachment: clearable(linkAttachment, ctx),
      ...(type != null ? { type } : {}),
    },
  }
}

export const threads: ServiceMapperEntry = {
  mapper: mapThreads,
}
